/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as NotificationActionCreators from '@app/actions/NotificationActionCreators';
import {Switch} from '@app/components/form/Switch';
import styles from '@app/components/modals/tabs/notifications_tab/Notifications.module.css';
import {usePushSubscriptions} from '@app/hooks/usePushSubscriptions';
import * as PushSubscriptionService from '@app/services/push/PushSubscriptionService';
import {isDesktop} from '@app/utils/NativeUtils';
import * as NotificationUtils from '@app/utils/NotificationUtils';
import {isInstalledPwa, isPwaOnMobileOrTablet} from '@app/utils/PwaUtils';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type {FC} from 'react';

interface NotificationsProps {
	browserNotificationsEnabled: boolean;
	unreadMessageBadgeEnabled: boolean;
}

export const Notifications: FC<NotificationsProps> = observer(
	({browserNotificationsEnabled, unreadMessageBadgeEnabled}) => {
		const {i18n, t} = useLingui();

		const isPwa = isInstalledPwa();
		const isPwaMobile = isPwaOnMobileOrTablet();
		const {subscriptions, loading, refresh} = usePushSubscriptions(isPwaMobile);

		const handleToggleNotifications = (value: boolean) => {
			if (!value) {
				NotificationActionCreators.permissionDenied(i18n, true);
				return;
			}

			if (typeof Notification === 'undefined') {
				void NotificationUtils.requestPermission(i18n);
				return;
			}

			// iOS Safari requires `Notification.requestPermission()` to run
			// synchronously from a user gesture, so dispatch it before any
			// awaits. Also auto-register a push subscription when running as a
			// PWA, since that is the path users expect "Enable" to take.
			const permissionPromise: Promise<NotificationPermission> =
				Notification.permission === 'granted' ? Promise.resolve('granted') : Notification.requestPermission();

			void (async () => {
				const permission = await permissionPromise;
				NotificationUtils.handlePermissionResult(i18n, permission);
				if (permission === 'granted' && isPwaMobile) {
					await PushSubscriptionService.registerPushSubscription();
					await refresh();
				}
			})();
		};

		const handleToggleUnreadBadge = (value: boolean) => {
			NotificationActionCreators.toggleUnreadMessageBadge(value);
		};

		const handleRegisterPushSubscription = () => {
			if (typeof Notification === 'undefined') return;

			const permissionPromise: Promise<NotificationPermission> =
				Notification.permission === 'granted' ? Promise.resolve('granted') : Notification.requestPermission();

			void (async () => {
				const permission = await permissionPromise;
				NotificationUtils.handlePermissionResult(i18n, permission);
				if (permission !== 'granted') return;

				await PushSubscriptionService.registerPushSubscription();
				await refresh();
			})();
		};

		const handleForgetPushSubscriptions = async () => {
			await PushSubscriptionService.unregisterAllPushSubscriptions();
			await refresh();
		};

		const pushStatusMessage = loading
			? t`Loading push subscriptions…`
			: subscriptions.length > 0
				? t`${subscriptions.length} active subscription(s)`
				: t`No push subscriptions registered yet.`;

		return (
			<div className={styles.container}>
				<p className={styles.description}>
					{isDesktop()
						? t`Configure how you receive desktop notifications.`
						: isPwa
							? t`Configure how you receive notifications from the Fluxer app.`
							: t`Configure how you receive notifications in your browser.`}
				</p>

				<div className={styles.switchesContainer}>
					<Switch
						label={
							isDesktop()
								? t`Enable Desktop Notifications`
								: isPwa
									? t`Enable Notifications`
									: t`Enable Browser Notifications`
						}
						description={
							isDesktop()
								? t`Uses the OS notification center. For per-channel/per-community controls, right-click a community icon and open Notification Settings.`
								: isPwa
									? t`Get notified when you receive messages. You may need to allow notifications for Fluxer in your device settings. For per-channel/per-community controls, open Notification Settings from a community's menu.`
									: t`Get notified when you receive messages. You may need to allow notifications in your browser settings. For per-channel/per-community controls, right-click a community icon and open Notification Settings.`
						}
						value={browserNotificationsEnabled}
						onChange={handleToggleNotifications}
					/>

					<Switch
						label={t`Enable unread message badge`}
						description={t`Shows a red badge on the app icon when you have unread messages.`}
						value={unreadMessageBadgeEnabled}
						onChange={handleToggleUnreadBadge}
					/>
				</div>

				{isPwaMobile && (
					<div className={styles.pushSection}>
						<div>
							<h3 className={styles.pushHeading}>{t`Push subscriptions for this device`}</h3>
							<p className={styles.pushDescription}>
								{t`Fluxer uses push notifications when installed as a mobile PWA. Registering ensures the gateway can reach your device even when the app is backgrounded.`}
							</p>
						</div>

						<div className={styles.pushButtons}>
							<button type="button" className={styles.pushButton} onClick={handleRegisterPushSubscription}>
								{subscriptions.length > 0 ? t`Refresh push subscription` : t`Enable push for this device`}
							</button>

							<button
								type="button"
								className={`${styles.pushButton} ${styles.pushButtonSecondary}`}
								onClick={handleForgetPushSubscriptions}
								disabled={subscriptions.length === 0}
							>
								{t`Forget subscriptions`}
							</button>
						</div>

						<p className={styles.pushStatus}>{pushStatusMessage}</p>

						{subscriptions.length > 0 && (
							<ul className={styles.pushList}>
								{subscriptions.map((subscription) => (
									<li key={subscription.subscription_id} className={styles.pushListItem}>
										<span>{subscription.user_agent ?? t`Unknown device`}</span>
										<span>{subscription.subscription_id}</span>
									</li>
								))}
							</ul>
						)}
					</div>
				)}
			</div>
		);
	},
);
